// TODO: [PROF-4641] change this file to use camelized properties
/* eslint-disable camelcase */
import { UNDETERMINED_FUTURE } from "PFApp/constants/dates";
import { getGrowlId, useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useIsBookingModulePermitted } from "PFApp/use_is_booking_module_permitted";
import { Icon } from "PFComponents/icon";
import { LoadingDots } from "PFComponents/loading_dots";
import { Modal } from "PFComponents/modal";
import { utc } from "PFCore/helpers/date";
import { useResponseErrors } from "PFCore/helpers/use_response_errors";
import { useBooking } from "PFCore/hooks/queries/bookings/use_booking";
import { useBookingDelete } from "PFCore/hooks/queries/bookings/use_booking_delete";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { Booking } from "PFTypes/booking";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useRefreshBookingData } from "../../hooks/use_refresh_booking_data";
import { deleteBookingTemplate } from "../../services/api";
import { BookingSelectOption } from "../booking_questions/booking_questions.types";
import { GroupBookingsQuestions } from "../booking_questions/group_bookings_questions";
import {
  GroupBookingsQuestionsMode,
  GroupBookingsQuestionsOption
} from "../booking_questions/group_bookings_questions.types";
import { SpecificTimeBookingsQuestions } from "../booking_questions/specific_time_bookings_questions";
import css from "./delete_booking_modal.module.scss";
import { SingleBookingsModalContent } from "./single_bookings_modal_content";

const DELETE_BOOKING_SUCCESS_ACTION_ID = getGrowlId("bm-delete-booking");

export type DeleteBookingModalData = {
  bookingId: number;
  profileFullName: string;
  onDeleted?: (booking: Booking, deleteOptionSelected: BookingSelectOption) => Promise<void> | void;
  isSingleDayBookingTemplate?: boolean;
};

type DeleteBookingModalProps = {
  onClose: VoidFunction;
} & DeleteBookingModalData;

export const DeleteBookingModal = ({
  bookingId,
  profileFullName,
  onClose,
  onDeleted,
  isSingleDayBookingTemplate
}: DeleteBookingModalProps) => {
  const { data: booking, isFetching } = useBooking(bookingId, { enabled: !!bookingId });
  const { delete: deleteBooking } = useBookingDelete(bookingId);

  const { booking_group_id, booking_template_id, activity } = booking ?? {};
  const refreshBookingData = useRefreshBookingData();
  const [deleteOptionSelected, setDeleteOptionSelected] = useState<BookingSelectOption>(
    BookingSelectOption.This
  );
  const [deleteGroupOptionSelected, setDeleteGroupOptionSelected] = useState<GroupBookingsQuestionsOption>(
    GroupBookingsQuestionsOption.GroupThis
  );

  const { formatISODate } = useDateFormatter();

  const isRepeated = Number.isInteger(booking_template_id);
  const isGrouped = Number.isInteger(booking_group_id);

  const { showGrowl } = useGrowlStoreActions();
  const { t } = useTranslation(["bookingModule", "translation"]);
  const isBookingPermitted = useIsBookingModulePermitted();
  const { getFirstError } = useResponseErrors();

  const isDeleteGroup = [GroupBookingsQuestionsOption.GroupAll].includes(deleteGroupOptionSelected);

  const handleClick = async () => {
    if (!booking) {
      return;
    }

    const { profile_id, id } = booking;

    try {
      const isDeleteFutureOrAll = [BookingSelectOption.Future, BookingSelectOption.All].includes(
        deleteOptionSelected
      );
      const isDeleteFuture = deleteOptionSelected === BookingSelectOption.Future;

      const defaultPayload = isDeleteGroup ? { booking_group_id } : { profile_id };

      const dateRangeFuture = {
        min: formatISODate(utc(booking.start_date)),
        max: formatISODate(UNDETERMINED_FUTURE)
      };

      if (booking_template_id) {
        await deleteBookingTemplate(isDeleteGroup ? undefined : booking_template_id, {
          ...defaultPayload,
          dateRange:
            deleteOptionSelected === BookingSelectOption.This
              ? { min: booking.start_date, max: booking.end_date }
              : isDeleteFuture
                ? dateRangeFuture
                : undefined
        });
      } else {
        await deleteBooking({
          ...defaultPayload,
          id: isDeleteFutureOrAll || isDeleteGroup ? undefined : id,
          activityId: !booking.booking_group_id && isDeleteFutureOrAll ? (activity?.id as number) : undefined,
          dateRange: isDeleteFuture ? dateRangeFuture : undefined
        });
      }

      refreshBookingData({
        bookingIds: [id],
        bookingGroupId: booking_group_id,
        activityId: activity?.id,
        profileIds: [profile_id],
        refreshGroup: deleteGroupOptionSelected === GroupBookingsQuestionsOption.GroupAll,
        refreshProfile: isRepeated
      });

      showGrowl({
        id: DELETE_BOOKING_SUCCESS_ACTION_ID,
        kind: "success",
        message: t("bookingModule:bookings.delete.success"),
        hidePreviousByTypeAndId: true,
        ttl: 5000
      });
      onDeleted && (await onDeleted(booking, deleteOptionSelected));
      onClose && onClose();
    } catch (response) {
      const error = getFirstError(response);
      showGrowl({
        message: error || t("translation:somethingWentWrong"),
        kind: "error"
      });
    }
  };

  return (
    <Modal
      title={
        <div className={css.title}>
          <Icon name="calendar-delete" size="lg" />
          {t("bookingModule:bookings.delete.deleteBooking")}
        </div>
      }
      labelOK={t("translation:continueText")}
      onOK={handleClick}
      onClose={onClose}
      onCancel={onClose}
      kindOK="danger"
    >
      <div>
        <div className={css.body}>
          {isFetching || !booking ? (
            <LoadingDots />
          ) : isBookingPermitted && (isGrouped || isRepeated) && !isSingleDayBookingTemplate ? (
            <>
              {isGrouped && (
                <GroupBookingsQuestions
                  mode={GroupBookingsQuestionsMode.Delete}
                  value={deleteGroupOptionSelected}
                  onChange={setDeleteGroupOptionSelected}
                  userName={profileFullName}
                  bookingGroupId={booking_group_id!}
                />
              )}
              {isRepeated && (
                <SpecificTimeBookingsQuestions
                  label={t("bookingModule:bookings.delete.body.question")}
                  value={deleteOptionSelected}
                  onChange={setDeleteOptionSelected}
                  booking={booking}
                />
              )}
            </>
          ) : (
            <SingleBookingsModalContent
              booking={booking}
              activity={activity}
              userName={profileFullName}
              value={deleteOptionSelected}
              onChange={setDeleteOptionSelected}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
